<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import TwilioVoice from './TwilioVoice.vue';
import ChannelSelector from 'dashboard/components/ChannelSelector.vue';

// Initialize global voice providers registry (defensive check for load order)
if (!window.chatwootVoiceProviders) {
  window.chatwootVoiceProviders = [];
}

// Register Twilio provider (only if not already registered)
if (!window.chatwootVoiceProviders.find(p => p.key === 'twilio')) {
  window.chatwootVoiceProviders.push({
    key: 'twilio',
    title: 'Twilio',
    description: 'Manage voice calls with Twilio enterprise reliability',
    icon: 'i-woot-twilio',
    component: TwilioVoice,
    order: 1, // Lower number = higher priority
  });
}

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const selectedProvider = computed(() => route.query.provider);
const showProviderSelection = computed(() => !selectedProvider.value);
const showConfiguration = computed(() => Boolean(selectedProvider.value));

// Get all registered providers and sort by order
const availableProviders = computed(() => {
  const providers = [...(window.chatwootVoiceProviders || [])];
  return providers.sort((a, b) => (a.order || 999) - (b.order || 999));
});

// Get component for selected provider
const getProviderComponent = providerKey => {
  const provider = window.chatwootVoiceProviders?.find(
    p => p.key === providerKey
  );
  return provider?.component;
};

const selectProvider = providerValue => {
  router.push({
    name: route.name,
    params: route.params,
    query: { provider: providerValue },
  });
};
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <!-- Provider Selection Screen -->
    <div v-if="showProviderSelection">
      <div class="mb-10 text-left">
        <h1 class="mb-2 text-lg font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.ADD.VOICE.SELECT_PROVIDER.TITLE') }}
        </h1>
        <p class="text-sm leading-relaxed text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.VOICE.SELECT_PROVIDER.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex gap-6 justify-start flex-wrap">
        <ChannelSelector
          v-for="provider in availableProviders"
          :key="provider.key"
          :title="provider.title"
          :description="provider.description"
          :icon="provider.icon"
          @click="selectProvider(provider.key)"
        />
      </div>
    </div>

    <!-- Configuration Screen (Dynamic Component) -->
    <component
      :is="getProviderComponent(selectedProvider)"
      v-else-if="showConfiguration"
    />
  </div>
</template>
